import { formatDateOnly } from '@/lib/utils/dateUtils';

/**
 * The Verify screen's quarantine stock prompt — its words and its rules, pure so they are pinned by
 * verifyQuarantine.test.ts. A scan the server refuses because the unit is in quarantine
 * (`extensions.reason` VERIFY_UNIT_QUARANTINED, isVerifyQuarantineRefusal) opens the prompt instead
 * of a bare refusal toast: it says where the unit is recorded, and offers "Stock & verify" — stock
 * it into a stock zone and verify it on this order in one step — or Cancel, which changes nothing.
 * The shapes below are `salesOrderVerifyQuarantine` (SalesOrderVerify.data.tsx).
 */

export type VerifyQuarantineZone = {
  id: string;
  name?: string | null;
  zoneType?: string | null;
};

export type VerifyQuarantineOption = {
  /** BULK / UPC: the quarantined balance. Null for a serial. */
  balanceId?: string | null;
  /** Where it is recorded: a quarantine zone, or a stock zone holding it quarantined in place. */
  zone?: VerifyQuarantineZone | null;
  /** The location the stock zone must be at. */
  locationId?: string | null;
  lot?: string | null;
  expiration?: string | null;
  quarantined: number;
  /** The zone itself when it is a stock zone, else the zone Pick confirmed for the product. */
  suggestedStockZone?: VerifyQuarantineZone | null;
};

export type VerifyQuarantineReason = 'serial' | 'lot' | 'exhausted';

export type VerifyQuarantineView = {
  lineKey: string;
  productName: string;
  kind: string;
  reason: VerifyQuarantineReason | string;
  serialNumber?: string | null;
  /** Units the scan verifies (1, or a typed quantity): the least a bulk stock may move. */
  quantity: number;
  remaining: number;
  options: VerifyQuarantineOption[];
  canStock: boolean;
};

/** What "Stock & verify" sends besides the scanned unit. */
export type VerifyStockChoice = {
  stockZoneId: string;
  balanceId: string | null;
  /** BULK / UPC only; null for a serial. */
  quantity: number | null;
};

export type VerifyStockResult = {
  lineKey?: string | null;
  addedQuantity?: number | null;
  stockedQuantity?: number | null;
  stockZone?: { name?: string | null } | null;
};

export const VERIFY_STOCK_FALLBACK_MESSAGE = 'Could not stock this unit. Please try again.';

const isSerial = (view: VerifyQuarantineView) => view.reason === 'serial';
const units = (n: number) => `${n} unit${n === 1 ? '' : 's'}`;

/** A stable key for an option (a serial's one option has no balance). */
export const quarantineOptionKey = (option: VerifyQuarantineOption, index: number) =>
  option.balanceId || `option-${index}`;

/** `Lot ABC · Exp 2027-01-31` — how one quarantined record is named. */
export const quarantineLotLabel = (option: VerifyQuarantineOption | null | undefined) => {
  if (!option) return '';
  const expiration = formatDateOnly(option.expiration);
  return [option.lot?.trim() ? `Lot ${option.lot.trim()}` : 'No lot', expiration && `Exp ${expiration}`]
    .filter(Boolean)
    .join(' · ');
};

/** The line under the product name: the serial (and its lot), or the lot in question. */
export const quarantineUnitLabel = (
  view: VerifyQuarantineView,
  option: VerifyQuarantineOption | null | undefined
) => {
  if (isSerial(view)) {
    return [`Serial ${view.serialNumber || ''}`.trim(), quarantineLotLabel(option)]
      .filter(Boolean)
      .join(' · ');
  }
  return view.reason === 'lot' ? quarantineLotLabel(option) : '';
};

const zoneName = (zone: VerifyQuarantineZone | null | undefined) => zone?.name || 'a quarantine zone';
const isInPlace = (option: VerifyQuarantineOption | null | undefined) =>
  option?.zone?.zoneType === 'stock';

/** Why the unit was not counted — where it is recorded, in the operator's terms. */
export const quarantineExplanation = (
  view: VerifyQuarantineView,
  option: VerifyQuarantineOption | null | undefined
) => {
  if (view.reason === 'exhausted') {
    const total = view.options.reduce((sum, o) => sum + (o.quarantined || 0), 0);
    return `No stocked ${view.productName} is left to verify — ${units(total)} ${
      total === 1 ? 'is' : 'are'
    } in quarantine. Not counted yet.`;
  }
  const subject = isSerial(view) ? 'This unit' : `Lot ${option?.lot || ''}`.trim();
  const where = isInPlace(option)
    ? `is quarantined in place in ${zoneName(option?.zone)}`
    : `is recorded in quarantine in ${zoneName(option?.zone)}`;
  return `${subject} ${where}, so it is not counted on this order yet.`;
};

/** The one caution the prompt always carries: quarantine is there for a reason. */
export const QUARANTINE_FIT_TO_SHIP_NOTE =
  'Only stock it if it is fit to ship. Otherwise cancel and set it aside.';

export const QUARANTINE_NO_PERMISSION_MESSAGE =
  'You do not have permission to stock inventory. Set the unit aside and ask a supervisor to stock it, or scan another unit.';

/** Where the offered stock zone came from, said beside it (null: nothing was offered). */
export const suggestedZoneNote = (option: VerifyQuarantineOption | null | undefined) => {
  if (!option?.suggestedStockZone) return null;
  return isInPlace(option) && option.suggestedStockZone.id === option.zone?.id
    ? 'Stocked in place — it is already in this zone.'
    : 'The zone Pick took this product from.';
};

/**
 * Why a typed quantity to stock cannot be sent, or null. At least the units this scan verifies
 * (they are stocked first), at most what the quarantined balance holds.
 */
export const stockQuantityProblem = (value: string, least: number, most: number) => {
  const text = (value || '').trim();
  if (!/^\d+$/.test(text)) return text ? 'Enter a whole number of units.' : null;
  const n = Number(text);
  if (n < least) return `Enter at least ${least} — the unit(s) you scanned are stocked first.`;
  if (n > most) return `Enter ${most} or fewer — only ${most} are in quarantine there.`;
  return null;
};

/** The success toast after "Stock & verify". */
export const stockedAndVerifiedToast = (view: VerifyQuarantineView, result: VerifyStockResult) => {
  const stocked = result.stockedQuantity ?? 0;
  const added = result.addedQuantity ?? 0;
  const zone = result.stockZone?.name ? ` to ${result.stockZone.name}` : '';
  if (stocked === 0) {
    return {
      title: 'Verified',
      text: `${view.productName} had already been stocked — it is verified on this order.`
    };
  }
  if (isSerial(view)) {
    return {
      title: 'Stocked & verified',
      text: `Serial ${view.serialNumber || ''} stocked${zone} and verified.`
    };
  }
  return {
    title: 'Stocked & verified',
    text:
      stocked === added
        ? `${units(stocked)} of ${view.productName} stocked${zone} and verified.`
        : `${units(stocked)} of ${view.productName} stocked${zone}; ${added} verified — scan the rest to verify them.`
  };
};

/** The toast after Cancel: nothing changed, and the operator should know the unit still is not counted. */
export const leftInQuarantineToast = (view: VerifyQuarantineView | null) => ({
  title: 'Left in quarantine',
  text: view
    ? `${isSerial(view) ? `Serial ${view.serialNumber || ''}` : view.productName} was not verified. It stays in quarantine.`
    : 'The unit was not verified. It stays in quarantine.'
});
